import { ContentHead } from '../../../lib/components';
import {
  ApprovedIcon,
  StatusRejectedIcon,
  DeleteIcon,
  DocumentIcon,
  StatusSuccessIcon
} from '../../../lib/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useStore } from '../../../hooks/useStore';
import { useState, useEffect } from 'react';
import { Receipt, ReceiptStatusEnum } from '@pxtra-client-lib-legacy/gen';
import { observer } from 'mobx-react-lite';
import { DataGrid, DataGridElement } from '../../../lib/components/compound/dataGrid';
import { Box, Dialog, DialogContent, Link, Typography } from '@mui/material';
import { BenefitIcon } from '../../../lib/components/compound/dataDisplay/benefitIcon';
import { ReceiptStatusBadge } from '../../../lib/components/compound/dataDisplay/receiptStatusBadge';
import Util from '../../../misc/util';
import { ReceiptActionDialog } from '../dialogs/action/component';
import { usePageContext } from '../../profile/partials/profilePagesPartial';
import { humanFileSize } from '../../../misc/humanFileSize';
import { useSnackbar } from 'notistack';
import { useDialog } from '../../../hooks/useDialog';
import { InformationDialogTitle } from '../../../lib/components/compound/dialogs/informationDialog/title';
import {
  ActionArea,
  ActionAreaProps
} from '../../../lib/components/compound/pages/head/actionArea';
import { ReceiptAction } from '../dialogs/action/viewModel';
import { ContentBox } from '../../../lib/components/compound/pages/contentBox';

export type ReceiptPageContext = 'employee' | 'receipts';

export const ReiceiptDetailView = observer(() => {
  const { receiptStore } = useStore();
  const { employeeId, receiptId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const context = usePageContext();

  const receipt = receiptStore.current;

  const overviewPath: Record<string, (id: string | undefined) => string> = {
    employee: (id?: string) => `/employees/${id}/receipts/overview`,
    receipts: () => '/receipts'
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<ReceiptAction>('delete');
  const snackbar = useSnackbar();
  const dialog = useDialog();

  const getErrorMessage = (message: string) => message.split('\n').at(0) ?? t('export:ERROR');
  const getErrorDetails = (message: string) => message.split('\n').slice(1).join('');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmitError = (body: any) => {
    snackbar.enqueueSnackbar({
      variant: 'detailedError',
      message: getErrorMessage(body?.message),
      details: getErrorDetails(body?.message)
    });
  };

  const renderTitle = (receipt?: Receipt) => {
    return receipt?.benefit ? t(`benefits:${receipt?.benefit}`) : '';
  };

  const approveMenuItemProps = {
    label: t('common:APPROVE'),
    icon: <ApprovedIcon />,
    isWarning: false,
    onClick: () => {
      receiptStore.approveCurrent().catch((e) => handleSubmitError(e.body));
    }
  };

  const settleMenuItemProps = {
    label: t('common:SETTLE'),
    icon: <StatusSuccessIcon />,
    isWarning: false,
    onClick: () => {
      setSelectedAction('settle');
      setIsDialogOpen(true);
    }
  };

  const rejectMenuItemProps = {
    label: t('common:REJECT'),
    icon: <StatusRejectedIcon />,
    isWarning: false,
    onClick: () => {
      setSelectedAction('reject');
      setIsDialogOpen(true);
    }
  };

  const deleteMenuItemProps = {
    label: t('common:DELETE'),
    icon: <DeleteIcon />,
    isWarning: true,
    onClick: () => {
      setSelectedAction('delete');
      setIsDialogOpen(true);
    }
  };

  const actionAreaProps: Record<ReceiptStatusEnum, ActionAreaProps> = {
    APPROVED: { threePointMenuConfig: [settleMenuItemProps, rejectMenuItemProps] },
    ARCHIVED: {},
    BILLED: {},
    SUBMITTED: {
      threePointMenuConfig: [approveMenuItemProps, rejectMenuItemProps, deleteMenuItemProps]
    },
    REJECTED: { threePointMenuConfig: [approveMenuItemProps] },
    DELETION_SUBMITTED: {},
    DRAFTED: {}
  };

  useEffect(() => {
    let isCanceled = false;
    const fetchReceipt = async () => {
      if (!isCanceled) {
        if (receiptId) {
          await receiptStore.fetchOne(receiptId);
        } else receiptStore.clearCurrent();
      }
    };
    fetchReceipt();
    return () => {
      isCanceled = true;
      receiptStore.clearCurrent();
    };
  }, [receiptId]);

  return (
    <>
      <ContentBox
        headComponent={
          <ContentHead
            headline={renderTitle(receipt)}
            renderBeforeHeadline={() =>
              receipt ? <BenefitIcon benefitKey={receipt?.benefit} /> : <></>
            }
            renderActions={() => (
              <ActionArea
                threePointMenuConfig={
                  actionAreaProps[receipt?.status ?? 'DRAFTED'].threePointMenuConfig
                }
              />
            )}
            showActions={true}
          />
        }
      >
        <Box>
          {receipt?.status ? (
            <ReceiptStatusBadge status={receipt?.status} hideCount={true} />
          ) : (
            <></>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: context === 'employee' ? '1fr 1fr' : '4fr 3fr',
              gridGap: '2rem',
              paddingTop: '24px'
            }}
          >
            <DataGrid spacing={2}>
              {context !== 'employee' ? (
                <DataGridElement
                  md={12}
                  value={receipt?.employee_name}
                  title={t('common:EMPLOYEES')}
                />
              ) : (
                <></>
              )}
              <DataGridElement
                md={12}
                value={Util.formatCurrency(receipt?.amount)}
                title={t('receipts:VALUE')}
              />
              <DataGridElement
                md={12}
                value={receipt?.date ? Util.formatDate(receipt?.date) : undefined}
                title={t('common:DATE')}
              />
              <DataGridElement md={12} value={receipt?.subject} title={t('receipts:SUBJECT')} />
              <DataGridElement md={12} value={receipt?.note} title={t('common:NOTE')} />
            </DataGrid>
            <Box
              sx={{
                maxWidth: '100%',
                height: '100%'
              }}
            >
              {receipt?.file?.name.endsWith('.pdf') ? (
                <Link href={receipt.file.url} target="_blank">
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: '16px',
                      padding: '16px',
                      boxSizing: 'border-box',
                      backgroundColor: 'grey.50'
                    }}
                  >
                    <DocumentIcon />
                    <Typography>{receipt.file?.name}</Typography>
                    <Typography>{humanFileSize(receipt.file.size)}</Typography>
                  </Box>
                </Link>
              ) : (
                <Box
                  sx={{
                    overflowY: 'hidden',
                    borderRadius: '16px',
                    paddingBottom: '100%',
                    position: 'relative',
                    filter: receipt?.file ? 'saturate(0)' : '',
                    opacity: receipt?.file ? '.4' : '1',
                    transition: 'filter 1s, opacity 1s, border-color 1s',
                    '&:hover': {
                      filter: 'saturate(1)',
                      opacity: '1',
                      cursor: receipt?.file ? 'zoom-in' : '',
                      borderColor: 'grey.50'
                    },
                    borderWidth: receipt?.file ? '1px' : '0',
                    borderColor: 'grey.100',
                    borderStyle: 'solid',
                    backgroundColor: 'grey.50',
                    backgroundImage: receipt?.file
                      ? `url(${Util.getURLfromBase64FileField(receipt.file)})`
                      : '',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    display: 'flex',
                    justifyItems: 'center'
                  }}
                  onClick={dialog.onOpen}
                >
                  {receipt?.file ? (
                    <></>
                  ) : (
                    <Typography
                      variant="h2"
                      sx={{
                        textAlign: 'center',
                        color: 'grey.100',
                        position: 'absolute',
                        width: '100%',
                        paddingY: 'calc(50% - 16px)'
                      }}
                    >
                      {t('receipts:NO_RECEIPT')}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </ContentBox>
      <ReceiptActionDialog
        open={isDialogOpen}
        action={selectedAction}
        count={1}
        onConfirm={async (note?: string) => {
          if (selectedAction === 'delete') {
            await receiptStore?.deleteCurrent();
            navigate(overviewPath[context](employeeId));
          } else if (selectedAction === 'reject') {
            receiptStore?.rejectCurrent(note).catch((e) => handleSubmitError(e.body));
          } else if (selectedAction === 'settle') {
            receiptStore?.settleCurrent().catch((e) => handleSubmitError(e.body));
          }
        }}
        onClose={() => setIsDialogOpen(false)}
      />
      <Dialog
        open={dialog.show}
        onClose={dialog.onClose}
        maxWidth="xl"
        PaperProps={{ className: 'shadow-900' }}
      >
        <InformationDialogTitle title={receipt?.subject ?? ''} onClose={dialog.onClose} />
        <DialogContent>
          {receipt?.file ? (
            <img
              src={Util.getURLfromBase64FileField(receipt.file)}
              style={{ maxWidth: '100%' }}
              alt={receipt.subject ?? ''}
            />
          ) : (
            <></>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
});
